import { Controller } from "@hotwired/stimulus"
import { createConsumer } from "@rails/actioncable"
import { get, normalize } from "../forum/api_client"
import { loadTranslations, t } from "../forum/i18n"
import { escapeHtml, loadingSkeletonHtml, paginationHtml, timeAgo, participantAvatarsHtml } from "../forum/renderer"

export default class extends Controller {
  static targets = ["content"]
  static values = { slug: String, categoryId: String, canCreate: Boolean }

  connect() {
    loadTranslations()
    this.currentPage = 1
    this.load()
    this.subscribeToCategoryChannel()
  }

  disconnect() {
    if (this.channel) {
      this.channel.unsubscribe()
      this.channel = null
    }
  }

  subscribeToCategoryChannel() {
    const categoryId = this.categoryIdValue
    if (!categoryId) return

    const consumer = createConsumer()
    this.channel = consumer.subscriptions.create(
      { channel: 'ForumCategoryChannel', category_id: categoryId },
      { received: (data) => this.handleBroadcast(data) }
    )
  }

  handleBroadcast(data) {
    if (data.action === 'topic_updated' && data.topic) {
      const topic = normalize(data.topic)
      this.updateTopicRow(topic)
    }
  }

  updateTopicRow(topic) {
    const row = this.element.querySelector(`tr[data-topic-id="${topic.id}"]`)
    if (!row) return

    const repliesCount = Math.max(0, (topic.posts_count || 1) - 1)
    const viewsCount = topic.views_count || 0
    const activityDate = topic.last_posted_at || topic.bumped_at || topic.created_at
    const participants = topic.participants || []

    const repliesCell = row.querySelector('.forum-col-replies .forum-num')
    if (repliesCell) repliesCell.textContent = repliesCount

    const viewsCell = row.querySelector('.forum-col-views .forum-num')
    if (viewsCell) viewsCell.textContent = viewsCount

    const activityCell = row.querySelector('.forum-col-activity .forum-activity-time')
    if (activityCell) {
      activityCell.textContent = activityDate ? timeAgo(activityDate) : '-'
      activityCell.title = activityDate ? new Date(activityDate).toLocaleString('pl-PL') : ''
    }

    const usersCell = row.querySelector('.forum-col-users')
    if (usersCell) {
      usersCell.innerHTML = participantAvatarsHtml(participants, topic.participants_count)
    }

    row.style.transition = 'background-color 0.5s ease'
    row.style.backgroundColor = '#fff3cd'
    setTimeout(() => { row.style.backgroundColor = '' }, 2000)
  }

  async load() {
    this.contentTarget.innerHTML = loadingSkeletonHtml(5)
    try {
      const response = await get(`/categories/${this.slugValue}`, { page: this.currentPage })
      this.meta = response.meta
      this.render(normalize(response.data), normalize(response.pinned_topics), normalize(response.topics), response.meta)
    } catch (e) {
      this.contentTarget.innerHTML = `<div class="alert alert-danger">${escapeHtml(e.message)}</div>`
    }
  }

  render(category, pinnedTopics, topics, meta) {
    let html = ''

    html += this.renderHeader(category)

    if (pinnedTopics && pinnedTopics.length > 0) {
      html += this.renderPinnedTopics(pinnedTopics)
    }

    const children = category.children || []
    if (children.length > 0) {
      html += this.renderSubcategories(children)
    }

    html += this.renderTopics(topics, meta, category)

    this.contentTarget.innerHTML = html
    this.bindPagination()
  }

  renderHeader(category) {
    const desc = category.description
      ? `<div class="forum-page-desc">${escapeHtml(category.description)}</div>`
      : ''
    return `
      <div class="forum-category-header mb-4">
        <h1 class="forum-page-title">${escapeHtml(category.name)}</h1>
        ${desc}
      </div>`
  }

  renderPinnedTopics(topics) {
    const rows = topics.map(topic => this.renderTopicRow(topic, true)).join('')

    return `
      <div class="forum-category-block mb-4">
        <h3 class="forum-maintitle">${t('announcements')}</h3>
        <div class="forum-table-wrap">
          <table class="forum-table forum-table--topics">
            <thead>
              <tr>
                <th class="forum-col-topic" colspan="2">${t('forum_table.topic')}</th>
                <th class="forum-col-users"></th>
                <th class="forum-col-replies">${t('forum_table.replies')}</th>
                <th class="forum-col-views">${t('forum_table.views')}</th>
                <th class="forum-col-activity">${t('forum_table.last_post')}</th>
              </tr>
            </thead>
            <tbody>${rows}</tbody>
          </table>
        </div>
      </div>`
  }

  renderSubcategories(children) {
    const totalTopics = children.reduce((s, c) => s + (c.topics_count || 0), 0)
    const totalPosts = children.reduce((s, c) => s + (c.posts_count || 0), 0)

    const rows = children.map(sub => {
      const iconHtml = sub.icon
        ? `<i class="mdi ${escapeHtml(sub.icon)}" style="color: ${escapeHtml(sub.color || '#6c757d')}; font-size: 1.75rem;"></i>`
        : '<i class="fa-solid fa-folder text-primary" style="font-size: 1.75rem;"></i>'
      const descHtml = sub.description
        ? `<div class="forum-desc">${escapeHtml(sub.description)}</div>`
        : ''
      const scopeTags = (sub.scope_tag_keys || []).map(key =>
        `<span class="badge bg-secondary me-1">${t(`scope_tags.${key}`)}</span>`
      ).join('')
      const scopeHtml = scopeTags ? `<div class="forum-scope-tags">${scopeTags}</div>` : ''

      let lastPostHtml = ''
      if (sub.last_post) {
        const lp = sub.last_post
        lastPostHtml = `
          <div class="forum-last-post small text-muted">
            <a href="/forum/topics/${lp.topic_id}" title="${escapeHtml(lp.topic_title || '')}">${escapeHtml((lp.topic_title || '').substring(0, 40))}</a>
            <br>${t('forum_table.last_post_by', { author: lp.author || t('unknown'), time: timeAgo(lp.created_at) })}
          </div>`
      }

      return `
        <tr class="forum-forum-row">
          <td class="forum-icon-cell">${iconHtml}</td>
          <td>
            <div class="forum-name">
              <a href="/forum/categories/${escapeHtml(sub.slug)}">${escapeHtml(sub.name)}</a>
            </div>
            ${descHtml}${scopeHtml}
          </td>
          <td class="forum-stats-cell">
            <ul class="forum-stats-list">
              <li>${t('topic_count', { count: sub.topics_count || 0 })}</li>
              <li>${t('post_count', { count: sub.posts_count || 0 })}</li>
            </ul>
            ${lastPostHtml}
          </td>
        </tr>`
    }).join('')

    return `
      <div class="forum-category-block mb-4">
        <h3 class="forum-maintitle">${t('subfora')}</h3>
        <div class="forum-summary-bar">
          ${t('stats_summary')}: ${t('summary.subcategories', { count: children.length })}, ${t('summary.topics', { count: totalTopics })}, ${t('summary.posts', { count: totalPosts })}
        </div>
        <div class="forum-table-wrap">
          <table class="forum-table"><tbody>${rows}</tbody></table>
        </div>
      </div>`
  }

  renderTopicRow(topic, pinned = false) {
    const iconHtml = pinned
      ? '<i class="fa-solid fa-thumbtack text-primary" style="font-size: 1.25rem;"></i>'
      : topic.locked
        ? '<i class="fa-solid fa-lock text-warning" style="font-size: 1.25rem;"></i>'
        : '<i class="fa-solid fa-comment text-muted" style="font-size: 1.25rem;"></i>'

    const repliesCount = Math.max(0, (topic.posts_count || 1) - 1)
    const viewsCount = topic.views_count || 0
    const participants = topic.participants || []
    const activityDate = topic.last_posted_at || topic.bumped_at || topic.created_at

    const authorHtml = topic.author
      ? `<span class="forum-topic-author text-muted">${escapeHtml(topic.author.full_name)}</span>`
      : ''

    return `
      <tr class="forum-topic-row" data-topic-id="${topic.id}">
        <td class="forum-col-icon">${iconHtml}</td>
        <td class="forum-col-topic">
          <div class="forum-topic-title">
            <a href="/forum/topics/${topic.id}">${escapeHtml(topic.title)}</a>
          </div>
          ${authorHtml}
        </td>
        <td class="forum-col-users">${participantAvatarsHtml(participants, topic.participants_count)}</td>
        <td class="forum-col-replies"><span class="forum-num">${repliesCount}</span></td>
        <td class="forum-col-views"><span class="forum-num">${viewsCount}</span></td>
        <td class="forum-col-activity"><span class="forum-activity-time" title="${activityDate ? new Date(activityDate).toLocaleString('pl-PL') : ''}">${activityDate ? timeAgo(activityDate) : '-'}</span></td>
      </tr>`
  }

  renderTopics(topics, meta, category) {
    const canCreate = meta.can_create_topic
    const newTopicBtn = canCreate
      ? `<a href="/forum/categories/${escapeHtml(category.slug)}/topics/new" class="btn btn-sm btn-light"><i class="fa-solid fa-plus me-1"></i>${t('new_topic')}</a>`
      : ''

    let topicsHtml
    if (topics && topics.length > 0) {
      const rows = topics.map(topic => this.renderTopicRow(topic)).join('')
      topicsHtml = `
        <table class="forum-table forum-table--topics">
          <thead>
            <tr>
              <th class="forum-col-topic" colspan="2">${t('forum_table.topic')}</th>
              <th class="forum-col-users"></th>
              <th class="forum-col-replies">${t('forum_table.replies')}</th>
              <th class="forum-col-views">${t('forum_table.views')}</th>
              <th class="forum-col-activity">${t('forum_table.last_post')}</th>
            </tr>
          </thead>
          <tbody>${rows}</tbody>
        </table>`
    } else {
      topicsHtml = `<div class="forum-empty-state p-4 text-center text-muted">${t('no_topics')}</div>`
    }

    const pagination = paginationHtml(meta)

    return `
      <div class="forum-category-block">
        <h3 class="forum-maintitle d-flex align-items-center justify-content-between">
          <span>${t('topics')}</span>
          ${newTopicBtn}
        </h3>
        <div class="forum-table-wrap">${topicsHtml}</div>
      </div>
      <div class="mt-3" data-forum-category-target="pagination">${pagination}</div>`
  }

  bindPagination() {
    this.element.querySelectorAll('[data-page]').forEach(link => {
      link.addEventListener('click', (e) => {
        e.preventDefault()
        this.currentPage = parseInt(link.dataset.page, 10)
        this.load()
        window.scrollTo({ top: 0, behavior: 'smooth' })
      })
    })
  }
}
